import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  FaDownload,
  FaCopy,
  FaCheck,
  FaPalette,
  FaChevronDown,
  FaChevronRight,
  FaFolderOpen,
  FaMousePointer,
  FaFileCode,
  FaFileAlt,
  FaExchangeAlt,
  FaSave,
} from 'react-icons/fa'
import { useParams, useNavigate } from 'react-router-dom'
import { AppHeader } from '@/components/shared/AppHeader'
import { useUserSettings } from '@/hooks/useUserSettings'
import { GOOGLE_FONTS } from '@/types'
import {
  type ThemeElement,
  type ElementConfig,
  ELEMENT_CATEGORIES,
  ELEMENT_SCHEMAS,
  configToClasses,
  classesToConfig,
} from '@/components/theme-editor/types'
import { ElementConfigPanel } from '@/components/theme-editor/ElementConfigPanel'
import { LivePreview } from '@/components/theme-editor/LivePreview'
import { ThemeSelectorModal } from '@/components/theme-editor/ThemeSelectorModal'
import { ReplaceColorsModal } from '@/components/theme-editor/ReplaceColorsModal'
import { customThemeRepository } from '@/lib/repositories'
import debounce from '@/utils/debounce'
import type { ThemePreset } from '@/data/themes.generated'

// Initialize configs with defaults
function getInitialConfigs(): Record<ThemeElement, ElementConfig> {
  const configs: Partial<Record<ThemeElement, ElementConfig>> = {}
  for (const [element, schema] of Object.entries(ELEMENT_SCHEMAS)) {
    configs[element as ThemeElement] = { ...schema.defaults }
  }
  return configs as Record<ThemeElement, ElementConfig>
}

export function ThemeEditorPage() {
  const { id: themeId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNewTheme = !themeId || themeId === 'new'
  const { settings } = useUserSettings()
  const darkMode = settings.editor.darkMode

  const [isLoaded, setIsLoaded] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [persistedId, setPersistedId] = useState<string | undefined>(
    isNewTheme ? undefined : themeId
  )

  const [themeName, setThemeName] = useState('My Custom Theme')
  const [bodyFont, setBodyFont] = useState('Inter')
  const [headingFont, setHeadingFont] = useState('')
  const [selectedElement, setSelectedElement] = useState<ThemeElement>('h1')
  const [configs, setConfigs] = useState<Record<ThemeElement, ElementConfig>>(getInitialConfigs())
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    Container: true,
    Headings: true,
    Text: true,
    Links: true,
    Lists: false,
    Quote: false,
    Code: false,
    Table: false,
    Media: false,
  })
  const [copied, setCopied] = useState(false)
  const [showThemeSelector, setShowThemeSelector] = useState(false)
  const [showReplaceColors, setShowReplaceColors] = useState(false)
  const [inspectMode, setInspectMode] = useState(false)
  const [savedNotification, setSavedNotification] = useState(false)

  // Load existing theme from IndexedDB
  useEffect(() => {
    if (!isNewTheme && themeId) {
      customThemeRepository.getById(themeId).then((theme) => {
        if (theme) {
          setThemeName(theme.name)
          setConfigs(theme.configs as Record<ThemeElement, ElementConfig>)
          setSelectedElement((theme.selectedElement as ThemeElement) || 'h1')
        }
        setIsLoaded(true)
        // Small delay so initial load doesn't trigger auto-save
        setTimeout(() => setIsInitialized(true), 100)
      })
    } else {
      setIsLoaded(true)
      setTimeout(() => setIsInitialized(true), 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeId])

  // Debounced auto-save to IndexedDB
  const debouncedSave = useRef(
    debounce(
      async (state: {
        persistedId: string | undefined
        themeName: string
        selectedElement: ThemeElement
        configs: Record<ThemeElement, ElementConfig>
        darkMode: boolean
        setPersistedId: (id: string) => void
        navigate: (path: string, opts?: { replace?: boolean }) => void
      }) => {
        try {
          if (state.persistedId) {
            await customThemeRepository.update(state.persistedId, {
              name: state.themeName,
              configs: state.configs as Record<string, Record<string, string | undefined>>,
              selectedElement: state.selectedElement,
              darkMode: state.darkMode,
            })
          } else {
            const created = await customThemeRepository.create({
              name: state.themeName,
              configs: state.configs as Record<string, Record<string, string | undefined>>,
              selectedElement: state.selectedElement,
              darkMode: state.darkMode,
            })
            state.setPersistedId(created.id)
            state.navigate(`/theme-editor/${created.id}`, { replace: true })
          }
        } catch (e) {
          console.error('Failed to auto-save theme:', e)
        }
      },
      800
    )
  ).current

  // Auto-save when state changes
  useEffect(() => {
    if (isInitialized && isLoaded) {
      debouncedSave({
        persistedId,
        themeName,
        selectedElement,
        configs,
        darkMode,
        setPersistedId,
        navigate,
      })
    }
  }, [
    themeName,
    selectedElement,
    configs,
    darkMode,
    isInitialized,
    isLoaded,
    persistedId,
    debouncedSave,
    navigate,
  ])

  // Update a single element's config
  const updateConfig = useCallback((element: ThemeElement, config: ElementConfig) => {
    setConfigs((prev) => ({
      ...prev,
      [element]: config,
    }))
  }, [])

  // Load theme from preset
  const loadThemePreset = useCallback((theme: ThemePreset) => {
    const newConfigs = getInitialConfigs()
    // Parse theme tailwindClasses and apply to configs
    if (theme.tailwindClasses) {
      for (const [element, classString] of Object.entries(theme.tailwindClasses)) {
        if (element in newConfigs) {
          // Parse the class string into individual config fields
          // This properly extracts fontSize, textColor, etc. from the classes
          newConfigs[element as ThemeElement] = classesToConfig(
            classString as string,
            element as ThemeElement
          )
        }
      }
    }
    setConfigs(newConfigs)
    // Only update theme name if current name is the default
    setThemeName((prev) => (prev === 'My Custom Theme' ? theme.name : prev))
    setBodyFont(theme.fontConfig.fontFamily)
    setHeadingFont(theme.fontConfig.headingFontFamily ?? '')
  }, [])

  // Load Google Fonts for live preview
  useEffect(() => {
    const fontsToLoad = [...new Set([bodyFont, headingFont].filter(Boolean))]
    document.querySelectorAll('link[data-theme-editor-font]').forEach((el) => el.remove())
    for (const font of fontsToLoad) {
      if (font === 'system-ui') continue
      const url = `https://fonts.googleapis.com/css2?family=${font.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = url
      link.setAttribute('data-theme-editor-font', font)
      document.head.appendChild(link)
    }
    return () => {
      document.querySelectorAll('link[data-theme-editor-font]').forEach((el) => el.remove())
    }
  }, [bodyFont, headingFont])

  // Toggle category expansion
  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }))
  }

  // Generate theme JSON
  const themeJSON = useMemo(() => {
    const classes: Record<string, string> = {}
    for (const [element, config] of Object.entries(configs)) {
      const classString = configToClasses(config, element as ThemeElement)
      if (classString) {
        classes[element] = classString
      }
    }

    return {
      id: themeName.toLowerCase().replace(/\s+/g, '-'),
      name: themeName,
      description: 'Custom theme created with Theme Editor',
      category: 'custom',
      fontFamily: bodyFont,
      ...(headingFont ? { headingFontFamily: headingFont } : {}),
      classes,
    }
  }, [configs, themeName, bodyFont, headingFont])

  // Generate YAML format (compatible with theme files)
  const generateYAML = useCallback(() => {
    const lines: string[] = [
      '---',
      `id: ${themeJSON.id}`,
      `name: ${themeJSON.name}`,
      `description: ${themeJSON.description}`,
      `category: ${themeJSON.category}`,
      `fontFamily: ${themeJSON.fontFamily}`,
      ...(themeJSON.headingFontFamily ? [`headingFontFamily: ${themeJSON.headingFontFamily}`] : []),
      '',
      'preview:',
      "  bgColor: '#ffffff'",
      "  textColor: '#1f2937'",
      "  accentColor: '#3b82f6'",
      `  headingFont: ${themeJSON.fontFamily}`,
      `  bodyFont: ${themeJSON.fontFamily}`,
      `  sampleHeading: ${themeJSON.name}`,
      '  sampleText: Custom theme created with Theme Editor.',
      '  style: clean',
      '',
      'behavior:',
      '  shouldOpenLinksInNewTab: true',
      '  shouldShowLineNumbers: true',
      '',
      'classes:',
    ]

    for (const [element, classString] of Object.entries(themeJSON.classes)) {
      lines.push(`  ${element}: ${classString}`)
    }

    lines.push('---', '', `# ${themeJSON.name}`, '', 'Your content goes here.')

    return lines.join('\n')
  }, [themeJSON])

  // Copy theme to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(themeJSON, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      console.error('Failed to copy')
    }
  }

  // Download theme as JSON (compatible with /editor)
  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(themeJSON, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${themeJSON.id}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Download theme as YAML (markdown file, compatible with default themes)
  const downloadYAML = () => {
    const yaml = generateYAML()
    const blob = new Blob([yaml], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${themeJSON.id}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // State for download dropdown
  const [showDownloadMenu, setShowDownloadMenu] = useState(false)

  // Manual save (force immediate persist)
  const saveNow = useCallback(async () => {
    try {
      const data = {
        name: themeName,
        configs: configs as Record<string, Record<string, string | undefined>>,
        selectedElement,
        darkMode,
      }
      if (persistedId) {
        await customThemeRepository.update(persistedId, data)
      } else {
        const created = await customThemeRepository.create(data)
        setPersistedId(created.id)
        navigate(`/theme-editor/${created.id}`, { replace: true })
      }
      setSavedNotification(true)
      setTimeout(() => setSavedNotification(false), 2000)
    } catch (e) {
      console.error('Failed to save theme:', e)
    }
  }, [themeName, configs, selectedElement, darkMode, persistedId, navigate])

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${darkMode ? 'dark' : ''}`}>
      <AppHeader
        title="Theme Editor"
        titleIcon={<FaPalette className="w-4 h-4 text-blue-500" />}
        leftContent={
          <input
            type="text"
            value={themeName}
            onChange={(e) => setThemeName(e.target.value)}
            className="ml-4 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            placeholder="Theme name..."
          />
        }
        iconButtons={
          <>
            {/* Load Theme Button */}
            <button
              onClick={() => setShowThemeSelector(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <FaFolderOpen className="w-3 h-3" />
              Load Theme
            </button>

            {/* Replace Colors Button */}
            <button
              onClick={() => setShowReplaceColors(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title="Find and replace colors across all elements"
            >
              <FaExchangeAlt className="w-3 h-3" />
              Replace Colors
            </button>

            {/* Save Theme */}
            <button
              onClick={saveNow}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title="Save theme"
            >
              {savedNotification ? (
                <>
                  <FaCheck className="w-3 h-3 text-green-500" />
                  Saved!
                </>
              ) : (
                <>
                  <FaSave className="w-3 h-3" />
                  Save
                </>
              )}
            </button>

            {/* Inspect Mode Toggle */}
            <button
              onClick={() => setInspectMode(!inspectMode)}
              className={`p-2 rounded-md transition-colors ${
                inspectMode
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              title={inspectMode ? 'Disable inspect mode' : 'Enable inspect mode'}
            >
              <FaMousePointer className="w-4 h-4" />
            </button>
          </>
        }
        rightContent={
          <>
            {/* Copy Button */}
            <button
              onClick={copyToClipboard}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {copied ? (
                <>
                  <FaCheck className="w-3 h-3 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <FaCopy className="w-3 h-3" />
                  Copy JSON
                </>
              )}
            </button>

            {/* Download Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <FaDownload className="w-3 h-3" />
                Download
                <FaChevronDown className="w-3 h-3" />
              </button>
              {showDownloadMenu && (
                <>
                  {/* Backdrop to close menu */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowDownloadMenu(false)} />
                  {/* Dropdown menu */}
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    <button
                      onClick={() => {
                        downloadJSON()
                        setShowDownloadMenu(false)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-md"
                    >
                      <FaFileCode className="w-4 h-4" />
                      Download JSON
                    </button>
                    <button
                      onClick={() => {
                        downloadYAML()
                        setShowDownloadMenu(false)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-md"
                    >
                      <FaFileAlt className="w-4 h-4" />
                      Download markdown
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        }
      />

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden bg-gray-100 dark:bg-gray-950">
        {/* Left Sidebar - Element List */}
        <aside className="w-56 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-3">
            <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Elements
            </h2>
            <nav className="space-y-1">
              {ELEMENT_CATEGORIES.map((category) => {
                const CategoryIcon = category.icon
                return (
                  <div key={category.name}>
                    {/* Category Header */}
                    <button
                      onClick={() => toggleCategory(category.name)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                    >
                      {expandedCategories[category.name] ? (
                        <FaChevronDown className="w-3 h-3 text-gray-400" />
                      ) : (
                        <FaChevronRight className="w-3 h-3 text-gray-400" />
                      )}
                      <CategoryIcon className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      <span>{category.name}</span>
                    </button>

                    {/* Category Elements */}
                    {expandedCategories[category.name] && (
                      <div className="ml-4 mt-1 space-y-0.5">
                        {category.elements.map((element) => (
                          <button
                            key={element}
                            onClick={() => setSelectedElement(element)}
                            className={`w-full text-left px-2 py-1 text-sm rounded-md transition-colors ${
                              selectedElement === element
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                            }`}
                          >
                            {element}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </nav>
          </div>
        </aside>

        {/* Center - Element Configuration */}
        <div className="w-80 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <ElementConfigPanel
            element={selectedElement}
            config={configs[selectedElement]}
            onChange={(config) => updateConfig(selectedElement, config)}
          />
          {selectedElement === 'body' && (
            <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Google Fonts
              </h3>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Body Font
                </label>
                <select
                  value={bodyFont}
                  onChange={(e) => setBodyFont(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                >
                  {GOOGLE_FONTS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  Heading Font
                </label>
                <select
                  value={headingFont}
                  onChange={(e) => setHeadingFont(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Same as body</option>
                  {GOOGLE_FONTS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Right - Live Preview */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0 flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">Live Preview</h2>
            {inspectMode && (
              <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                <FaMousePointer className="w-3 h-3" />
                Click an element to edit
              </span>
            )}
          </div>
          <div className={`flex-1 overflow-auto ${inspectMode ? 'cursor-crosshair' : ''}`}>
            <LivePreview
              configs={configs}
              highlightElement={selectedElement}
              inspectMode={inspectMode}
              fontFamily={bodyFont}
              headingFontFamily={headingFont || undefined}
              onSelectElement={(element) => {
                setSelectedElement(element)
              }}
            />
          </div>
        </div>
      </div>

      {/* Theme Selector Modal */}
      <ThemeSelectorModal
        isOpen={showThemeSelector}
        onClose={() => setShowThemeSelector(false)}
        onSelectTheme={loadThemePreset}
        currentThemeId={themeJSON.id}
      />

      {/* Replace Colors Modal */}
      <ReplaceColorsModal
        isOpen={showReplaceColors}
        onClose={() => setShowReplaceColors(false)}
        configs={configs}
        onReplace={setConfigs}
      />
    </div>
  )
}
